import React from 'react';
import { Button } from '@grubhubprod/gh-cookbook-react/dist';
import { useFormContext } from 'react-hook-form/dist/index.ie11';
import {
  Breakpoint,
  useDeviceSize,
} from '@grubhubprod/umami-library/dist/hooks/useDeviceSize';
import {
  getPrimaryButtonText,
  LineOfCreditStepPage,
} from 'src/components/selfService/features/Signup/steps/LineOfCreditStep/LineOfCreditStep.utils';
import { AsyncTaskStatus } from 'src/utilities/useAsyncTask';
import { defaultDays } from 'src/components/selfService/features/Signup/steps/LineOfCreditStep/LineOfCreditStep.constants';
import { getWindow } from 'src/utilities/window';
import { ILocFormFields } from 'src/models/LineOfCreditModelUtils';
import { SignupStepFooterActions } from 'src/components/selfService/features/Signup/components/SignupStepFooterActions/SignupStepFooterActions';

interface SignupStepFooterContentProps {
  currentPage: LineOfCreditStepPage;
  submissionStatus: AsyncTaskStatus;
  isUpdatingStep: boolean;
  isOnboardingPage: boolean;
  handleBack: () => void;
  handleSkip: () => void;
  handleSubmit: () => void;
}

export function SignupStepFooterContent({
  currentPage,
  submissionStatus,
  isUpdatingStep,
  isOnboardingPage,
  handleBack,
  handleSkip,
  handleSubmit,
}: SignupStepFooterContentProps): JSX.Element {
  const { watch, formState } = useFormContext<ILocFormFields>();
  const { breakpoint } = useDeviceSize(getWindow());

  const selectedDays = watch('days', defaultDays);
  const frequency = watch('frequency');
  const budgetLength = watch('budgetLength', '');

  const { isValid } = formState;

  const isMobile = breakpoint < Breakpoint.sm;

  function isFormValid(): boolean {
    let hasValidSelectedDays = true;

    if (budgetLength === 'day' && frequency === 'repeats') {
      hasValidSelectedDays = selectedDays.some((day) => day !== false);
    }

    return isValid && hasValidSelectedDays;
  }

  function shouldDisableSubmitButton(): boolean {
    if (currentPage === LineOfCreditStepPage.ONBOARDING) {
      return false;
    }

    // we want to keep the submission button disabled when the submission
    // is complete so no extra clicks happen before the next step renders
    return (
      !isFormValid() ||
      submissionStatus === AsyncTaskStatus.IN_PROGRESS ||
      submissionStatus === AsyncTaskStatus.COMPLETE
    );
  }

  return (
    <SignupStepFooterActions
      isUpdating={isUpdatingStep}
      back={
        <Button
          variant="secondary"
          buttonSize="medium"
          block={isMobile}
          onClick={handleBack}
        >
          Back
        </Button>
      }
      skip={
        <Button
          variant="tertiary"
          buttonSize="medium"
          block={isMobile}
          onClick={handleSkip}
        >
          Skip for now
        </Button>
      }
      next={
        <Button
          variant="primary"
          buttonSize="medium"
          block={isMobile}
          disabled={shouldDisableSubmitButton()}
          isLoading={submissionStatus === AsyncTaskStatus.IN_PROGRESS}
          onClick={handleSubmit}
        >
          {getPrimaryButtonText(isOnboardingPage, !!isUpdatingStep)}
        </Button>
      }
    />
  );
}
